
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */



const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h4 {...{
      "id": "hirer-configuration-queries"
    }}>{`Hirer configuration queries`}</h4>
    <p>{`SEEK’s support team configures hirers to work with the SEEK API:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><a parentName="p" {...{
            "href": "/auth/hirer-relationships"
          }}>{`Hirer relationships`}</a>{` are added as part of onboarding hirers to an integration partner.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><a parentName="p" {...{
            "href": "/use-cases/job-posting/application-methods#linking-out-to-an-external-apply-form"
          }}>{`Link out applications`}</a>{` are enabled for hirers that comply with our link out terms of use.`}</p>
      </li>
    </ul>
    <p>{`Previously, integration partners haven’t had visibility into SEEK’s configuration.
This required manual reconciliation with our partners to avoid unexpected failures.`}</p>
    <p>{`We’ve extended the SEEK API to support automatically detecting hirer configuration:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`The `}<a parentName="p" {...{
            "href": "https://developer.seek.com/schema/#/named-type/HiringOrganization"
          }}><inlineCode parentName="a">{`HiringOrganization`}</inlineCode></a>{` object now has a `}<inlineCode parentName="p">{`seekApiCapabilities`}</inlineCode>{` field.
This allows you to query a hirer’s active relationships and allowed application methods.`}</p>
        <p parentName="li">{`You can retrieve a `}<inlineCode parentName="p">{`HiringOrganization`}</inlineCode>{` using either the `}<a parentName="p" {...{
            "href": "https://developer.seek.com/schema/#/query/hiringOrganization"
          }}><inlineCode parentName="a">{`hiringOrganization`}</inlineCode>{` query`}</a>{` or the `}<a parentName="p" {...{
            "href": "https://developer.seek.com/schema/#/query/seekAnzAdvertiser"
          }}><inlineCode parentName="a">{`seekAnzAdvertiser`}</inlineCode>{` query`}</a>{`:`}</p>
        <pre parentName="li"><code parentName="pre" {...{
            "className": "language-scoobie-merged-code",
            "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"label=\\\"By hirer ID\\\"\",\"value\":\"query ($id: String!) {\\n  hiringOrganization(id: $id) {\\n    name\\n    seekApiCapabilities {\\n      relationshipTypeCodes\\n      applicationMethodCodes\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":19,\"column\":3,\"offset\":832},\"end\":{\"line\":29,\"column\":6,\"offset\":1045},\"indent\":[3,3,3,3,3,3,3,3,3,3]}},{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"label=\\\"By SEEK advertiser ID\\\"\",\"value\":\"query ($id: Int!) {\\n  seekAnzAdvertiser(id: $id) {\\n    name\\n    seekApiCapabilities {\\n      relationshipTypeCodes\\n      applicationMethodCodes\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":31,\"column\":3,\"offset\":1049},\"end\":{\"line\":41,\"column\":6,\"offset\":1268},\"indent\":[3,3,3,3,3,3,3,3,3,3]}}]",
            "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"label": "\\\"By",
            "hirer": true,
            "ID\\\"\",\"value\":\"query": true,
            "($id:": true,
            "String!)": true,
            "{\\n": true,
            "": true,
            "hiringOrganization(id:": true,
            "$id)": true,
            "name\\n": true,
            "seekApiCapabilities": true,
            "relationshipTypeCodes\\n": true,
            "applicationMethodCodes\\n": true,
            "}\\n": true,
            "}\\n}\",\"position\":{\"start\":{\"line\":19,\"column\":3,\"offset\":832},\"end\":{\"line\":29,\"column\":6,\"offset\":1045},\"indent\":[3,3,3,3,3,3,3,3,3,3]}},{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"label": "\\\"By",
            "SEEK": true,
            "advertiser": true,
            "Int!)": true,
            "seekAnzAdvertiser(id:": true,
            "}\\n}\",\"position\":{\"start\":{\"line\":31,\"column\":3,\"offset\":1049},\"end\":{\"line\":41,\"column\":6,\"offset\":1268},\"indent\":[3,3,3,3,3,3,3,3,3,3]}}]": true
          }}>{`query ($id: String!) {
  hiringOrganization(id: $id) {
    name
    seekApiCapabilities {
      relationshipTypeCodes
      applicationMethodCodes
    }
  }
}
`}</code></pre>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The new `}<a parentName="p" {...{
            "href": "https://developer.seek.com/schema/#/query/hiringOrganizations"
          }}><inlineCode parentName="a">{`hiringOrganizations`}</inlineCode>{` query`}</a>{` allows you to list all the SEEK hirers you have an active relationship with.
You can optionally filter on the hirer’s name or specific relationship types.`}</p>
        <p parentName="li">{`You can use this to retrieve a complete list of your SEEK hirers, including their identifiers and API capabilities:`}</p>
        <pre parentName="li"><code parentName="pre" {...{
            "className": "language-scoobie-merged-code",
            "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query ($schemeId: String!, $first: Int) {\\n  hiringOrganizations(schemeId: $schemeId, first: $first) {\\n    edges {\\n      node {\\n        id {\\n          value\\n        }\\n        name\\n        seekApiCapabilities {\\n          relationshipTypeCodes\\n          applicationMethodCodes\\n        }\\n      }\\n    }\\n    pageInfo {\\n      hasNextPage\\n      endCursor\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":48,\"column\":3,\"offset\":1589},\"end\":{\"line\":69,\"column\":6,\"offset\":2004},\"indent\":[3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"schemeId\\\": \\\"seekAnzPublicTest\\\",\\n  \\\"first\\\": 3\\n}\",\"position\":{\"start\":{\"line\":71,\"column\":3,\"offset\":2008},\"end\":{\"line\":76,\"column\":6,\"offset\":2081},\"indent\":[3,3,3,3,3]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"hiringOrganizations\\\": {\\n    \\\"edges\\\": [\\n      {\\n        \\\"node\\\": {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n          },\\n          \\\"name\\\": \\\"Acme Corp\\\",\\n          \\\"seekApiCapabilities\\\": {\\n            \\\"relationshipTypeCodes\\\": [\\n              \\\"ApplicationExport\\\",\\n              \\\"ApplicationPrefill\\\",\\n              \\\"JobPosting\\\",\\n              \\\"ProactiveSourcing\\\"\\n            ],\\n            \\\"applicationMethodCodes\\\": [\\\"ApplicationUri\\\"]\\n          }\\n        }\\n      },\\n      {\\n        \\\"node\\\": {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"seekAnzPublicTest:organization:seek:A3HL8E4w\\\"\\n          },\\n          \\\"name\\\": \\\"Contoso\\\",\\n          \\\"seekApiCapabilities\\\": {\\n            \\\"relationshipTypeCodes\\\": [\\n              \\\"ApplicationExport\\\",\\n              \\\"ApplicationPrefill\\\"\\n            ],\\n            \\\"applicationMethodCodes\\\": [\\\"ApplicationUri\\\"]\\n          }\\n        }\\n      },\\n      {\\n        \\\"node\\\": {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"seekAnzPublicTest:organization:seek:AYAWCibZ\\\"\\n          },\\n          \\\"name\\\": \\\"Fabrikam\\\",\\n          \\\"seekApiCapabilities\\\": {\\n            \\\"relationshipTypeCodes\\\": [\\n              \\\"ApplicationExport\\\",\\n              \\\"ApplicationPrefill\\\"\\n            ],\\n            \\\"applicationMethodCodes\\\": []\\n          }\\n        }\\n      }\\n    ],\\n    \\\"pageInfo\\\": {\\n      \\\"hasNextPage\\\": true,\\n      \\\"endCursor\\\": \\\"seekAnzPublicTest:hiringOrganizationCursor:seek:AYAWCibZ\\\"\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":78,\"column\":3,\"offset\":2085},\"end\":{\"line\":136,\"column\":6,\"offset\":3650},\"indent\":[3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3]}}]",
            "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query": true,
            "($schemeId:": true,
            "String!,": true,
            "$first:": true,
            "Int)": true,
            "{\\n": true,
            "": true,
            "hiringOrganizations(schemeId:": true,
            "$schemeId,": true,
            "first:": true,
            "$first)": true,
            "edges": true,
            "node": true,
            "id": true,
            "value\\n": true,
            "}\\n": true,
            "name\\n": true,
            "seekApiCapabilities": true,
            "relationshipTypeCodes\\n": true,
            "applicationMethodCodes\\n": true,
            "pageInfo": true,
            "hasNextPage\\n": true,
            "endCursor\\n": true,
            "}\\n}\",\"position\":{\"start\":{\"line\":48,\"column\":3,\"offset\":1589},\"end\":{\"line\":69,\"column\":6,\"offset\":2004},\"indent\":[3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
            "\\\"schemeId\\\":": true,
            "\\\"seekAnzPublicTest\\\",\\n": true,
            "\\\"first\\\":": true,
            "3\\n}\",\"position\":{\"start\":{\"line\":71,\"column\":3,\"offset\":2008},\"end\":{\"line\":76,\"column\":6,\"offset\":2081},\"indent\":[3,3,3,3,3]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
            "\\\"hiringOrganizations\\\":": true,
            "\\\"edges\\\":": true,
            "[\\n": true,
            "\\\"node\\\":": true,
            "\\\"id\\\":": true,
            "\\\"value\\\":": true,
            "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n": true,
            "},\\n": true,
            "\\\"name\\\":": true,
            "\\\"Acme": true,
            "Corp\\\",\\n": true,
            "\\\"seekApiCapabilities\\\":": true,
            "\\\"relationshipTypeCodes\\\":": true,
            "\\\"ApplicationExport\\\",\\n": true,
            "\\\"ApplicationPrefill\\\",\\n": true,
            "\\\"JobPosting\\\",\\n": true,
            "\\\"ProactiveSourcing\\\"\\n": true,
            "],\\n": true,
            "\\\"applicationMethodCodes\\\":": true,
            "[\\\"ApplicationUri\\\"]\\n": true,
            "\\\"seekAnzPublicTest:organization:seek:A3HL8E4w\\\"\\n": true,
            "\\\"Contoso\\\",\\n": true,
            "\\\"ApplicationPrefill\\\"\\n": true,
            "\\\"seekAnzPublicTest:organization:seek:AYAWCibZ\\\"\\n": true,
            "\\\"Fabrikam\\\",\\n": true,
            "[]\\n": true,
            "\\\"pageInfo\\\":": true,
            "\\\"hasNextPage\\\":": true,
            "true,\\n": true,
            "\\\"endCursor\\\":": true,
            "\\\"seekAnzPublicTest:hiringOrganizationCursor:seek:AYAWCibZ\\\"\\n": true,
            "}\\n}\",\"position\":{\"start\":{\"line\":78,\"column\":3,\"offset\":2085},\"end\":{\"line\":136,\"column\":6,\"offset\":3650},\"indent\":[3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3,3]}}]": true
          }}>{`query ($schemeId: String!, $first: Int) {
  hiringOrganizations(schemeId: $schemeId, first: $first) {
    edges {
      node {
        id {
          value
        }
        name
        seekApiCapabilities {
          relationshipTypeCodes
          applicationMethodCodes
        }
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}
`}</code></pre>
      </li>
    </ul>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;